// Признак онлайн-услуги (Service.is_online): приём идёт по видеосвязи через Consilium.
// Бейдж — span с ролью кнопки, а не <button>: он стоит внутри ссылок и
// строк-кнопок, а кнопка в кнопке — невалидная разметка. Подсказка открывается по нажатию (на телефоне наведения нет)
// и дополнительно по наведению на десктопе. Подсказка рендерится порталом с
// фиксированной позицией — карточки услуг режут содержимое через overflow-hidden.
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Video, MonitorSmartphone, Clock, Mic } from 'lucide-react';

type Size = 'xs' | 'sm' | 'md';

interface OnlineBadgeProps {
  size?: Size;
  // onPrimary — на синем фоне (выбранная строка услуги у врача)
  tone?: 'solid' | 'onPrimary';
  className?: string;
}

const TONE = {
  solid: `text-[#eeeef0] bg-gradient-to-r from-[#0c63e9] to-[#3b82f6] dark:from-[#004dc3] dark:to-[#0c63e9]
    shadow-[0_2px_8px_rgba(12,99,233,0.25)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.35)]
    hover:shadow-[4px_4px_19px_0px_rgba(0,60,156,0.25)] hover:brightness-110`,
  onPrimary: 'text-[#eeeef0] bg-[#eeeef0]/15 ring-1 ring-inset ring-[#eeeef0]/45 hover:bg-[#eeeef0]/25',
};

const SIZE: Record<Size, { pill: string; icon: string }> = {
  xs: { pill: 'h-5 px-2 gap-1 text-[11px]', icon: 'w-3 h-3' },
  sm: { pill: 'h-7 px-3 gap-1.5 text-sm', icon: 'w-3.5 h-3.5' },
  md: { pill: 'h-10 px-4 gap-2 text-base', icon: 'w-5 h-5' },
};

const POPOVER_WIDTH = 288;
const GUTTER = 16;
const GAP = 10;

interface Position {
  top: number;
  left: number;
  arrowLeft: number;
  placement: 'top' | 'bottom';
}

export const OnlineBadge: React.FC<OnlineBadgeProps> = ({ size = 'sm', tone = 'solid', className = '' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);
  const buttonRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<number | undefined>(undefined);
  // Открыто наведением: клик по бейджу тогда закрепляет подсказку, а не закрывает её
  const hoverOpened = useRef(false);
  const pinned = useRef(false);
  const popoverId = useId();

  const place = useCallback(() => {
    const btn = buttonRef.current;
    const pop = popoverRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const width = Math.min(POPOVER_WIDTH, vw - GUTTER * 2);
    const height = pop?.offsetHeight ?? 180;
    const center = r.left + r.width / 2;
    const left = Math.max(GUTTER, Math.min(center - width / 2, vw - GUTTER - width));
    // Над бейджем, если там хватает места под шапкой сайта (104px), иначе снизу
    const placement = r.top - height - GAP > 112 ? 'top' : 'bottom';
    const top = placement === 'top' ? r.top - height - GAP : r.bottom + GAP;
    setPos({ top, left, arrowLeft: Math.max(16, Math.min(center - left, width - 16)), placement });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
    else {
      hoverOpened.current = false;
      pinned.current = false;
    }
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  // Наведение — только для настоящей мыши: на тач-экране pointerenter
  // приходит перед click и открытие тут же схлопывалось бы обратно.
  const onPointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse') return;
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => {
      setOpen((v) => {
        if (!v) hoverOpened.current = true;
        return true;
      });
    }, 120);
  };
  const onPointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || pinned.current) return;
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setOpen(false), 160);
  };

  const s = SIZE[size];
  const points = [
    { Icon: MonitorSmartphone, text: t('online.pointBrowser') },
    { Icon: Clock, text: t('online.pointLink') },
    { Icon: Mic, text: t('online.pointDevices') },
  ];

  return (
    <>
      <span
        ref={buttonRef}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }
        }}
        onClick={(e) => {
          // Бейдж часто стоит внутри ссылки или label — не уводить со страницы
          // и не щёлкать чекбокс
          e.preventDefault();
          e.stopPropagation();
          window.clearTimeout(hoverTimer.current);
          if (open && hoverOpened.current) {
            hoverOpened.current = false;
            pinned.current = true;
            return;
          }
          hoverOpened.current = false;
          pinned.current = !open;
          setOpen(!open);
        }}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-label={`${t('online.badge')}. ${t('online.title')}`}
        className={`group relative inline-flex shrink-0 items-center rounded-full font-medium leading-none whitespace-nowrap
          cursor-pointer select-none align-middle transition-all duration-200 ${TONE[tone]}
          focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0c63e9] focus-visible:ring-offset-[#fdfdfd]
          dark:focus-visible:ring-[#599bff] dark:focus-visible:ring-offset-[#121212]
          ${s.pill} ${className}`}
        style={{ outline: 'none' }}
      >
        <Video className={`${s.icon} shrink-0`} strokeWidth={2.25} aria-hidden />
        {t('online.badge')}
        {/* «В эфире»: тихая пульсирующая точка — отличает бейдж от обычных меток */}
        <span className="relative flex h-1.5 w-1.5" aria-hidden>
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#eeeef0] opacity-70 motion-safe:animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#eeeef0]" />
        </span>
      </span>

      {open && createPortal(
        <div
          ref={popoverRef}
          id={popoverId}
          role="dialog"
          aria-label={t('online.title')}
          onPointerEnter={onPointerEnter}
          onPointerLeave={onPointerLeave}
          className="fixed z-[1000] rounded-2xl border border-[#d9e5f1] dark:border-[rgba(30,50,64,0.75)]
            bg-[#fdfdfd] dark:bg-[#1f1f1f] text-left
            shadow-[4px_4px_19px_0px_rgba(0,60,156,0.15)] dark:shadow-[0_8px_28px_rgba(0,0,0,0.55)]
            motion-safe:animate-[onlinePop_160ms_ease-out]"
          style={{
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            width: Math.min(POPOVER_WIDTH, window.innerWidth - GUTTER * 2),
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <style>{'@keyframes onlinePop{from{opacity:0;transform:translateY(4px) scale(.98)}to{opacity:1;transform:none}}'}</style>

          {/* Стрелка к бейджу */}
          {pos && (
            <span
              aria-hidden
              className={`absolute h-3 w-3 rotate-45 bg-[#fdfdfd] dark:bg-[#1f1f1f] border-[#d9e5f1] dark:border-[rgba(30,50,64,0.75)] ${
                pos.placement === 'top' ? '-bottom-1.5 border-r border-b' : '-top-1.5 border-l border-t'
              }`}
              style={{ left: pos.arrowLeft - 6 }}
            />
          )}

          <div className="relative p-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#c2dbff] dark:bg-[#192e4d] text-[#0c63e9] dark:text-[#599bff]">
                <Video className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-[15px] font-semibold leading-5 text-[#454545] dark:text-[#e4e4e4]">{t('online.title')}</p>
                <p className="text-xs leading-4 text-[#454545]/60 dark:text-[#e4e4e4]/60">{t('online.subtitle')}</p>
              </div>
            </div>

            <ul className="mt-3 space-y-2 border-t border-[#d9e5f1] dark:border-[#192e4d] pt-3">
              {points.map(({ Icon, text }) => (
                <li key={text} className="flex items-start gap-2.5 text-[13px] leading-[18px] text-[#454545] dark:text-[#e4e4e4]">
                  <Icon className="mt-px h-4 w-4 shrink-0 text-[#0c63e9] dark:text-[#599bff]" strokeWidth={2} aria-hidden />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

// Постоянная плашка там, где ошибиться дороже (страница услуги, подтверждение
// записи): тултип можно не открыть, эту строку — не заметить трудно.
export const OnlineNotice: React.FC<{ className?: string; compact?: boolean }> = ({ className = '', compact = false }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-[#c2dbff] dark:border-[#192e4d]
        bg-gradient-to-br from-[#d9e5f1] to-[#edeef2] dark:from-[#192e4d] dark:to-[#1f1f1f]
        ${compact ? 'p-3' : 'p-4 sm:p-5'} ${className}`}
    >
      <div className="flex items-start gap-3">
        <span className={`flex shrink-0 items-center justify-center rounded-xl bg-[#0c63e9] dark:bg-[#004dc3] text-[#eeeef0]
          shadow-[0_4px_12px_rgba(12,99,233,0.3)] ${compact ? 'h-8 w-8' : 'h-10 w-10'}`}>
          <Video className={compact ? 'h-4 w-4' : 'h-5 w-5'} strokeWidth={2} aria-hidden />
        </span>
        <div className="min-w-0">
          <p className={`font-semibold text-[#0c63e9] dark:text-[#599bff] ${compact ? 'text-sm' : 'text-base'}`}>
            {t('online.title')}
          </p>
          <p className={`mt-0.5 text-[#454545] dark:text-[#e4e4e4] ${compact ? 'text-xs leading-4' : 'text-sm leading-5'}`}>
            {t(compact ? 'online.noticeShort' : 'online.notice')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnlineBadge;
